import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus, X, Search, Star, StarOff, ExternalLink, Loader2, Trash2, Pencil,
  ArrowUp, ArrowDown, Sparkles, Mail, FileText, Table, HardDrive, Briefcase,
  BookOpen, Code, GraduationCap, ShoppingBag, Wallet, MessageCircle, Users,
  Camera, PlayCircle, MapPin, Landmark, FolderOpen, Globe,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// Whitelist of icons the backend may reference. Anything else → Globe.
const ICONS = {
  Sparkles, Mail, FileText, Table, HardDrive, Briefcase, BookOpen, Code,
  GraduationCap, ShoppingBag, Wallet, MessageCircle, Users, Camera,
  PlayCircle, MapPin, Landmark, FolderOpen, Globe,
};
const Icon = ({ slug, size = 20, className = "" }) => {
  const C = ICONS[slug] || Globe;
  return <C size={size} className={className} />;
};

const CATEGORY_LABELS = {
  ai: "AI Tools", work: "Work", study: "Study", shopping: "Shopping",
  banking: "Banking", social: "Social", business: "Business",
  government: "Government", travel: "Travel", entertainment: "Entertainment",
  other: "Other",
};

function ToolCard({ tool, onOpen, onFavorite, onEdit, onDelete }) {
  return (
    <div className="group relative rounded-2xl border border-stone-200 bg-white p-3 hover:border-[#E05E36]/50 hover:shadow-md transition-all" data-testid={`tool-card-${tool.tool_id}`}>
      <button
        type="button"
        onClick={() => onOpen(tool)}
        className="w-full text-left"
        data-testid={`tool-open-${tool.tool_id}`}
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#E05E36]/10 grid place-items-center shrink-0">
            <Icon slug={tool.icon_slug} className="text-[#E05E36]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-stone-900 text-[13px] truncate">{tool.name}</div>
            <div className="text-[10px] text-stone-500 truncate">{tool.url.replace(/^https?:\/\//, "").replace(/\/$/, "")}</div>
          </div>
          <ExternalLink size={12} className="text-stone-400 shrink-0" />
        </div>
      </button>
      {/* hover actions */}
      <div className="absolute top-2 right-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button type="button" onClick={() => onFavorite(tool)} title={tool.favorite ? "Unfavorite" : "Favorite"} data-testid={`tool-fav-${tool.tool_id}`} className="p-1 rounded-full bg-white border border-stone-200 hover:bg-amber-50">
          {tool.favorite ? <Star size={11} className="text-amber-500 fill-amber-500" /> : <StarOff size={11} className="text-stone-400" />}
        </button>
        <button type="button" onClick={() => onEdit(tool)} title="Edit" data-testid={`tool-edit-${tool.tool_id}`} className="p-1 rounded-full bg-white border border-stone-200 hover:bg-stone-50">
          <Pencil size={11} className="text-stone-500" />
        </button>
        <button type="button" onClick={() => onDelete(tool)} title="Delete" data-testid={`tool-del-${tool.tool_id}`} className="p-1 rounded-full bg-white border border-stone-200 hover:bg-red-50">
          <Trash2 size={11} className="text-red-500" />
        </button>
      </div>
      {tool.favorite && (
        <Star size={10} className="absolute top-2 left-2 text-amber-500 fill-amber-500" data-testid={`tool-fav-badge-${tool.tool_id}`} />
      )}
    </div>
  );
}

function ToolDialog({ initial, catalogCategories, onClose, onSave }) {
  const [name, setName]         = useState(initial?.name || "");
  const [url, setUrl]           = useState(initial?.url || "https://");
  const [category, setCategory] = useState(initial?.category || "other");
  const [iconSlug, setIconSlug] = useState(initial?.icon_slug || "Globe");
  const [busy, setBusy]         = useState(false);
  const [err, setErr]           = useState(null);
  const isEdit = !!initial?.tool_id;

  async function submit() {
    setErr(null);
    if (!name.trim()) return setErr("Name is required");
    if (!/^https?:\/\//i.test(url.trim())) return setErr("URL must start with http:// or https://");
    setBusy(true);
    try {
      const body = { name: name.trim(), url: url.trim(), category, icon_slug: iconSlug };
      await onSave(body);
      onClose();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Save failed.");
    } finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/50 grid place-items-center p-4" style={{ zIndex: "var(--z-modal)" }} data-testid="tool-dialog">
      <div className="bg-white rounded-3xl max-w-md w-full p-6 shadow-2xl">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">{isEdit ? "Edit shortcut" : "Add shortcut"}</div>
            <div className="mt-1 font-black text-stone-900 text-lg">{isEdit ? name : "New shortcut"}</div>
          </div>
          <button onClick={onClose} className="text-stone-500 hover:text-stone-900" data-testid="tool-dialog-close">
            <X size={20} />
          </button>
        </div>

        <div className="mt-4 space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">Name</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Gmail" className="mt-1 w-full rounded-xl border border-stone-200 focus:border-[#E05E36] focus:ring-2 focus:ring-[#E05E36]/20 outline-none p-2.5 text-sm" data-testid="tool-name" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">Official URL</label>
            <input type="url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://mail.google.com" className="mt-1 w-full rounded-xl border border-stone-200 focus:border-[#E05E36] focus:ring-2 focus:ring-[#E05E36]/20 outline-none p-2.5 text-sm font-mono" data-testid="tool-url" />
            <div className="text-[10px] text-stone-500 mt-1">Find2Near stores only a shortcut. Your account with this site remains with them.</div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">Category</label>
            <select value={category} onChange={(e) => setCategory(e.target.value)} className="mt-1 w-full rounded-xl border border-stone-200 focus:border-[#E05E36] focus:ring-2 focus:ring-[#E05E36]/20 outline-none p-2.5 text-sm bg-white" data-testid="tool-category">
              {(catalogCategories || Object.keys(CATEGORY_LABELS)).map((c) => (
                <option key={c} value={c}>{CATEGORY_LABELS[c] || c}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">Icon</label>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {Object.keys(ICONS).map((slug) => (
                <button key={slug} type="button" onClick={() => setIconSlug(slug)} className={`w-9 h-9 rounded-xl grid place-items-center border transition ${iconSlug === slug ? "border-[#E05E36] bg-[#E05E36]/10" : "border-stone-200 hover:border-stone-300"}`} data-testid={`tool-icon-${slug}`} title={slug}>
                  <Icon slug={slug} size={16} className={iconSlug === slug ? "text-[#E05E36]" : "text-stone-500"} />
                </button>
              ))}
            </div>
          </div>
        </div>

        {err && <div className="mt-3 text-sm text-red-600" data-testid="tool-dialog-error">{err}</div>}

        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-full text-sm font-bold text-stone-700 hover:bg-stone-100" data-testid="tool-dialog-cancel">Cancel</button>
          <button onClick={submit} disabled={busy} className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full text-sm font-bold text-white bg-[#E05E36] hover:bg-[#c94e2a] disabled:opacity-50" data-testid="tool-dialog-save">
            {busy ? <Loader2 size={14} className="animate-spin" /> : (isEdit ? "Save" : "Add")}
          </button>
        </div>
      </div>
    </div>
  );
}

function CatalogSheet({ onClose, onAdd, catalog }) {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return catalog;
    return catalog.filter((c) => c.name.toLowerCase().includes(needle) || c.url.includes(needle));
  }, [q, catalog]);
  return (
    <div className="fixed inset-0 bg-black/50 grid place-items-center p-4" style={{ zIndex: "var(--z-modal)" }} data-testid="catalog-sheet">
      <div className="bg-white rounded-3xl max-w-2xl w-full p-6 shadow-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <div className="flex items-start justify-between shrink-0">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">Quick add</div>
            <div className="mt-1 font-black text-stone-900 text-lg">Popular shortcuts</div>
          </div>
          <button onClick={onClose} className="text-stone-500 hover:text-stone-900" data-testid="catalog-close"><X size={20}/></button>
        </div>
        <div className="mt-3 relative shrink-0">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400"/>
          <input type="text" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="w-full pl-9 pr-3 py-2 rounded-full border border-stone-200 focus:border-[#E05E36] outline-none text-sm" data-testid="catalog-search" />
        </div>
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2 overflow-y-auto pr-1" data-testid="catalog-grid">
          {filtered.map((c) => (
            <button key={c.url} type="button" onClick={() => onAdd(c)} className="text-left rounded-2xl border border-stone-200 bg-white p-3 hover:border-[#E05E36]/50 hover:bg-[#E05E36]/5" data-testid={`catalog-add-${c.name.replace(/\s+/g,'-').toLowerCase()}`}>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-[#E05E36]/10 grid place-items-center shrink-0"><Icon slug={c.icon_slug} size={14} className="text-[#E05E36]"/></div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-stone-900 text-[13px] truncate">{c.name}</div>
                  <div className="text-[10px] text-stone-500 truncate">{CATEGORY_LABELS[c.category] || c.category}</div>
                </div>
                <Plus size={12} className="text-stone-400"/>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function MyTools() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [tools, setTools]     = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [catCats, setCatCats] = useState(Object.keys(CATEGORY_LABELS));
  const [q, setQ]             = useState("");
  const [activeCat, setActiveCat] = useState("all");
  const [dialog, setDialog]   = useState(null);
  const [showCatalog, setShowCatalog] = useState(false);
  const [err, setErr]         = useState(null);

  const load = useCallback(async () => {
    try {
      const [t, c] = await Promise.all([
        api.get("/tools"),
        api.get("/tools/catalog"),
      ]);
      setTools(t.data.tools || []);
      setCatalog(c.data.catalog || []);
      setCatCats(c.data.categories || Object.keys(CATEGORY_LABELS));
    } catch (e) {
      if (e?.response?.status === 401) navigate("/");
      else setErr(e?.response?.data?.detail || "Could not load your shortcuts.");
    }
  }, [navigate]);
  useEffect(() => { if (user) load(); }, [user, load]);

  const filtered = useMemo(() => {
    if (!tools) return null;
    const needle = q.trim().toLowerCase();
    return tools.filter((t) => {
      if (activeCat !== "all" && t.category !== activeCat) return false;
      if (needle && !(`${t.name} ${t.url}`.toLowerCase().includes(needle))) return false;
      return true;
    });
  }, [tools, q, activeCat]);

  async function saveNew(body) {
    const { data } = await api.post("/tools", body);
    setTools((old) => [...(old || []), data]);
  }
  async function saveEdit(body, id) {
    const { data } = await api.patch(`/tools/${id}`, body);
    setTools((old) => old.map((t) => t.tool_id === id ? data : t));
  }
  async function toggleFavorite(t) {
    const { data } = await api.patch(`/tools/${t.tool_id}`, { favorite: !t.favorite });
    setTools((old) => old.map((x) => x.tool_id === data.tool_id ? data : x));
  }
  async function del(t) {
    if (!window.confirm(`Remove "${t.name}" from your shortcuts?`)) return;
    await api.delete(`/tools/${t.tool_id}`);
    setTools((old) => old.filter((x) => x.tool_id !== t.tool_id));
  }
  function openTool(t) {
    // Open in a new tab — Find2Near is a doorway, not a container.
    window.open(t.url, "_blank", "noopener,noreferrer");
  }

  if (authLoading) return null;
  if (!user) {
    return (
      <div className="max-w-2xl mx-auto p-8 text-center text-stone-700" data-testid="tools-login-required">
        Please sign in to organize your tools.
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 pb-24">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-8">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-[#E05E36]">My Tools</div>
            <h1 className="heading text-3xl font-black text-stone-900" data-testid="tools-h1">One doorway to your digital world</h1>
            <p className="mt-1 text-sm text-stone-600 max-w-2xl">
              A launcher for the websites you already use. Find2Near opens the official destination — we never store your third-party passwords.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowCatalog(true)} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-bold text-stone-800 bg-white border border-stone-200 hover:bg-stone-100" data-testid="tools-open-catalog">
              <Sparkles size={14} /> Quick add
            </button>
            <button onClick={() => setDialog({})} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-bold text-white bg-[#E05E36] hover:bg-[#c94e2a]" data-testid="tools-open-new">
              <Plus size={14} /> Add
            </button>
          </div>
        </div>

        {/* Filter row */}
        <div className="mt-5 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400"/>
            <input type="text" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search your shortcuts…" className="w-full pl-9 pr-3 py-2 rounded-full border border-stone-200 focus:border-[#E05E36] focus:ring-2 focus:ring-[#E05E36]/20 outline-none text-sm bg-white" data-testid="tools-search"/>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {["all", ...catCats].map((c) => (
              <button key={c} onClick={() => setActiveCat(c)} className={`text-[11px] font-bold px-3 py-1.5 rounded-full border transition-colors ${activeCat === c ? "border-[#E05E36] bg-[#E05E36] text-white" : "border-stone-200 text-stone-700 bg-white hover:bg-stone-100"}`} data-testid={`tools-filter-${c}`}>
                {c === "all" ? "All" : (CATEGORY_LABELS[c] || c)}
              </button>
            ))}
          </div>
        </div>

        {err && <div className="mt-5 rounded-2xl border border-red-200 bg-red-50 p-3 text-sm text-red-700" data-testid="tools-load-error">{err}</div>}

        {tools === null && (
          <div className="mt-8 p-12 text-center rounded-3xl border border-stone-200 bg-white" data-testid="tools-loading">
            <Loader2 size={28} className="text-[#E05E36] animate-spin mx-auto" />
            <div className="mt-2 text-sm text-stone-600">Loading your shortcuts…</div>
          </div>
        )}

        {tools !== null && (filtered?.length || 0) === 0 && (
          <div className="mt-8 p-12 text-center rounded-3xl border-2 border-dashed border-stone-300 bg-white" data-testid="tools-empty">
            <div className="text-stone-800 font-black text-lg">No shortcuts yet.</div>
            <div className="text-[12px] text-stone-500 mt-1">Tap "Quick add" to pick from popular services, or "Add" to enter your own URL.</div>
            <button onClick={() => setShowCatalog(true)} className="mt-4 inline-flex items-center gap-1.5 px-5 py-2 rounded-full text-sm font-bold text-white bg-[#E05E36] hover:bg-[#c94e2a]" data-testid="tools-empty-quickadd">
              <Sparkles size={14} /> Browse popular tools
            </button>
          </div>
        )}

        {tools !== null && (filtered?.length || 0) > 0 && (
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="tools-grid">
            {filtered.map((t) => (
              <ToolCard
                key={t.tool_id} tool={t}
                onOpen={openTool}
                onFavorite={toggleFavorite}
                onEdit={(x) => setDialog(x)}
                onDelete={del}
              />
            ))}
          </div>
        )}
      </div>

      {dialog && (
        <ToolDialog
          initial={dialog.tool_id ? dialog : null}
          catalogCategories={catCats}
          onClose={() => setDialog(null)}
          onSave={async (body) => {
            if (dialog.tool_id) await saveEdit(body, dialog.tool_id);
            else await saveNew(body);
          }}
        />
      )}

      {showCatalog && (
        <CatalogSheet
          catalog={catalog}
          onClose={() => setShowCatalog(false)}
          onAdd={async (c) => {
            await saveNew({ name: c.name, url: c.url, category: c.category, icon_slug: c.icon_slug });
          }}
        />
      )}
    </div>
  );
}
